/**
 * Assistant state on the timeline document (docs/agent.md §4).
 *
 * Two things live here:
 *  - `status`: who is running the assistant right now, so other collaborators
 *    see "✨ Assistant is working (asked by sanjay)" rather than clips
 *    materialising with no explanation.
 *  - `proposals`: the replayable op logs behind proposal cards, keyed by id.
 *
 * Same wire-data discipline as chat.ts: everything here arrives from a peer and
 * anyone with the collaboration link can write arbitrary JSON into the document,
 * so the boundary between wire data and typed data is the validator below.
 * Invalid records are dropped, never asserted into existence.
 */
import * as Y from 'yjs';
import { genId } from '../format/helpers';
import type { Op } from '../agent/core/types';
import { isOp } from '../agent/proposal/oplog';
import { AGENT_STATE_ORIGIN, ROOT_AGENT } from './schema';

export type ProposalStatus = 'pending' | 'applied' | 'discarded' | 'conflict';

export interface ProposalRecord {
  id: string;
  runId: string;
  title: string;
  summary: string;
  ops: Op[];
  status: ProposalStatus;
  /** Display name of whoever asked for it. */
  by: string;
  at: number;
  appliedBy?: string;
  appliedAt?: number;
  /** Why it could not be applied, when status is 'conflict'. */
  conflict?: string;
}

export interface AgentStatus {
  runId: string;
  state: 'idle' | 'running';
  label: string;
  byName: string;
  at: number;
}

/** One archive request a step made, as it went over the wire. */
export interface TraceCall {
  site: string;
  action: string;
  data: unknown;
  ms: number;
  ok: boolean;
  cached?: boolean;
  httpStatus?: number;
  apiStatus?: number;
  bytes?: number;
  error?: string;
}

/** One tool call, compact enough to keep in the document. */
export interface TraceStep {
  seq: number;
  tool: string;
  label: string;
  state: 'ok' | 'error';
  error?: string;
  ms?: number;
  args?: Record<string, unknown>;
  opArgs?: Record<string, unknown>;
  calls?: TraceCall[];
}

/**
 * What one assistant run did, so it can still be examined after the run ends —
 * and after a reload, and by collaborators who did not invoke it.
 *
 * Written once, at the end of a run. Never incrementally: the record is a plain
 * JSON leaf, so N writes of a growing array put all N copies into the update log.
 */
export interface TraceRecord {
  runId: string;
  by: string;
  at: number;
  endReason: string;
  steps: TraceStep[];
  /** Steps dropped to fit the size cap, so the UI can admit the gap. */
  truncated?: number;
}

const STATUS_KEY = 'status';
const PROPOSALS_KEY = 'proposals';
const TRACES_KEY = 'traces';

/** Keep the newest N proposals; older resolved ones are pruned on write. */
export const MAX_PROPOSALS = 20;
/** Resolved proposals older than this are pruned even within the cap. */
const RESOLVED_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Traces are pruned harder than proposals and have no "never drop" case: losing
 * one costs you a debugging aid, whereas losing a pending proposal loses work
 * the user never got to review.
 */
export const MAX_TRACES = 10;
export const MAX_TRACE_STEPS = 200;
export const MAX_TRACE_BYTES = 24_000;
const TRACE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** Caps on individual strings, applied on read — a peer can write any length. */
const MAX_LABEL = 300;
const MAX_ERROR = 1000;

const STATUSES: ReadonlySet<string> = new Set<ProposalStatus>([
  'pending',
  'applied',
  'discarded',
  'conflict',
]);

function agentMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap<unknown>(ROOT_AGENT);
}

function proposalsMap(doc: Y.Doc): Y.Map<unknown> {
  const root = agentMap(doc);
  let existing = root.get(PROPOSALS_KEY) as Y.Map<unknown> | undefined;
  if (!(existing instanceof Y.Map)) {
    existing = new Y.Map();
    root.set(PROPOSALS_KEY, existing);
  }
  return existing;
}

export function isProposalRecord(value: unknown): value is ProposalRecord {
  if (typeof value !== 'object' || value === null) return false;
  const p = value as Record<string, unknown>;
  return (
    typeof p.id === 'string' &&
    typeof p.runId === 'string' &&
    typeof p.title === 'string' &&
    typeof p.summary === 'string' &&
    typeof p.by === 'string' &&
    typeof p.at === 'number' &&
    Number.isFinite(p.at) &&
    typeof p.status === 'string' &&
    STATUSES.has(p.status) &&
    Array.isArray(p.ops) &&
    p.ops.every(isOp)
  );
}

function isAgentStatus(value: unknown): value is AgentStatus {
  if (typeof value !== 'object' || value === null) return false;
  const s = value as Record<string, unknown>;
  return (
    typeof s.runId === 'string' &&
    (s.state === 'idle' || s.state === 'running') &&
    typeof s.label === 'string' &&
    typeof s.byName === 'string' &&
    typeof s.at === 'number'
  );
}

// ---------------------------------------------------------------------------
// reads
// ---------------------------------------------------------------------------

/**
 * A `running` status older than this is treated as abandoned.
 *
 * `idle` is written by the run's own teardown, which never happens if the tab
 * crashes, the laptop sleeps, or the network drops mid-run. Without a staleness
 * rule that leaves every OTHER collaborator looking at "✨ Assistant is working"
 * for the rest of the session — and because it lives in the document, the lie
 * survives reloads and outlives the person who caused it.
 *
 * Comfortably longer than the proxy's own upstream timeout, so a slow-but-alive
 * run is never mistaken for a dead one.
 */
export const STATUS_STALE_MS = 3 * 60 * 1000;

/**
 * `now` is injectable so the staleness rule can be tested without waiting or
 * mocking the clock.
 */
export function readStatus(doc: Y.Doc, now: number = Date.now()): AgentStatus | null {
  const raw = agentMap(doc).get(STATUS_KEY);
  if (!isAgentStatus(raw)) return null;
  if (raw.state === 'running' && now - raw.at > STATUS_STALE_MS) {
    // Reported as idle rather than dropped: callers still want `byName` for
    // "last run by …", and every consumer already branches on `state`.
    return { ...raw, state: 'idle', label: '' };
  }
  return raw;
}

/** All valid proposals, newest first. Invalid entries are dropped silently. */
export function readProposals(doc: Y.Doc): ProposalRecord[] {
  const out: ProposalRecord[] = [];
  const map = agentMap(doc).get(PROPOSALS_KEY);
  if (!(map instanceof Y.Map)) return out;
  map.forEach((value) => {
    if (isProposalRecord(value)) out.push(value);
  });
  return out.sort((a, b) => b.at - a.at);
}

export function readProposal(doc: Y.Doc, id: string): ProposalRecord | null {
  const map = agentMap(doc).get(PROPOSALS_KEY);
  if (!(map instanceof Y.Map)) return null;
  const raw = map.get(id);
  return isProposalRecord(raw) ? raw : null;
}

// ---------------------------------------------------------------------------
// writes
// ---------------------------------------------------------------------------

export function setStatus(doc: Y.Doc, status: Omit<AgentStatus, 'at'>): void {
  doc.transact(() => {
    agentMap(doc).set(STATUS_KEY, { ...status, at: Date.now() });
  }, AGENT_STATE_ORIGIN);
}

/**
 * Publish a proposal and return its id. Prunes on write: chat history already
 * grows without bound in this document and there is no reason to add a second
 * unbounded root.
 */
export function publishProposal(
  doc: Y.Doc,
  draft: { runId: string; title: string; summary: string; ops: Op[]; by: string },
): string {
  const record: ProposalRecord = {
    id: genId('prop'),
    runId: draft.runId,
    title: draft.title,
    summary: draft.summary,
    ops: draft.ops,
    status: 'pending',
    by: draft.by,
    at: Date.now(),
  };
  doc.transact(() => {
    const map = proposalsMap(doc);
    map.set(record.id, record);
    prune(map);
  }, AGENT_STATE_ORIGIN);
  return record.id;
}

/**
 * Replace a proposal's status. Records are plain JSON leaves (same convention as
 * chat messages), so an update rewrites the whole leaf rather than mutating a
 * nested map — last write wins, which is what we want for a status field two
 * people might click at once.
 */
export function updateProposal(
  doc: Y.Doc,
  id: string,
  patch: Partial<Pick<ProposalRecord, 'status' | 'appliedBy' | 'appliedAt' | 'conflict'>>,
): void {
  const existing = readProposal(doc, id);
  if (!existing) return;
  doc.transact(() => {
    proposalsMap(doc).set(id, { ...existing, ...patch });
  }, AGENT_STATE_ORIGIN);
}

function prune(map: Y.Map<unknown>): void {
  const now = Date.now();
  const records: ProposalRecord[] = [];
  map.forEach((value, key) => {
    if (isProposalRecord(value)) records.push(value);
    else map.delete(key); // junk from a peer: drop it while we are here
  });

  for (const record of records) {
    if (record.status !== 'pending' && now - record.at > RESOLVED_TTL_MS) {
      map.delete(record.id);
    }
  }

  // Still over the cap: drop the oldest, but never a pending one — an
  // unreviewed proposal disappearing is worse than exceeding the cap.
  const remaining = records
    .filter((r) => map.has(r.id))
    .sort((a, b) => a.at - b.at);
  let excess = remaining.length - MAX_PROPOSALS;
  for (const record of remaining) {
    if (excess <= 0) break;
    if (record.status === 'pending') continue;
    map.delete(record.id);
    excess -= 1;
  }
}

// ---------------------------------------------------------------------------
// traces
// ---------------------------------------------------------------------------

function tracesMap(doc: Y.Doc): Y.Map<unknown> {
  const root = agentMap(doc);
  let existing = root.get(TRACES_KEY) as Y.Map<unknown> | undefined;
  if (!(existing instanceof Y.Map)) {
    existing = new Y.Map();
    root.set(TRACES_KEY, existing);
  }
  return existing;
}

function clip(value: unknown, max: number): string {
  const s = typeof value === 'string' ? value : '';
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function sanitizeCall(value: unknown): TraceCall | null {
  if (!isPlainObject(value)) return null;
  if (typeof value.site !== 'string' || typeof value.action !== 'string') return null;
  const call: TraceCall = {
    site: clip(value.site, 200),
    action: clip(value.action, 60),
    data: value.data,
    ms: typeof value.ms === 'number' ? value.ms : 0,
    ok: value.ok === true,
  };
  if (value.cached === true) call.cached = true;
  if (typeof value.httpStatus === 'number') call.httpStatus = value.httpStatus;
  if (typeof value.apiStatus === 'number') call.apiStatus = value.apiStatus;
  if (typeof value.bytes === 'number') call.bytes = value.bytes;
  if (typeof value.error === 'string') call.error = clip(value.error, MAX_ERROR);
  return call;
}

/**
 * Validate one step. A malformed step is dropped on its own rather than
 * invalidating the whole trace — same principle as chat attachments, where one
 * bad clip card must not cost you the message it came with.
 */
function sanitizeStep(value: unknown): TraceStep | null {
  if (!isPlainObject(value)) return null;
  if (typeof value.seq !== 'number' || typeof value.tool !== 'string') return null;
  const step: TraceStep = {
    seq: value.seq,
    tool: clip(value.tool, 60),
    label: clip(value.label, MAX_LABEL),
    state: value.state === 'error' ? 'error' : 'ok',
  };
  if (typeof value.error === 'string') step.error = clip(value.error, MAX_ERROR);
  if (typeof value.ms === 'number') step.ms = value.ms;
  if (isPlainObject(value.args)) step.args = value.args;
  if (isPlainObject(value.opArgs)) step.opArgs = value.opArgs;
  if (Array.isArray(value.calls)) {
    const calls = value.calls.map(sanitizeCall).filter((c): c is TraceCall => c !== null);
    if (calls.length) step.calls = calls;
  }
  return step;
}

export function readTrace(doc: Y.Doc, runId: string): TraceRecord | null {
  return sanitizeTrace(tracesMap(doc).get(runId));
}

/** All valid traces, newest first. */
export function readTraces(doc: Y.Doc): TraceRecord[] {
  const out: TraceRecord[] = [];
  tracesMap(doc).forEach((value) => {
    const record = sanitizeTrace(value);
    if (record) out.push(record);
  });
  return out.sort((a, b) => b.at - a.at);
}

function sanitizeTrace(value: unknown): TraceRecord | null {
  if (!isPlainObject(value)) return null;
  if (typeof value.runId !== 'string' || !value.runId) return null;
  if (!Array.isArray(value.steps)) return null;
  const steps = value.steps
    .map(sanitizeStep)
    .filter((s): s is TraceStep => s !== null)
    // Capping on read as well as on write: the write cap only constrains what
    // THIS client produces, and every record here came from somewhere else.
    .slice(-MAX_TRACE_STEPS);
  const record: TraceRecord = {
    runId: clip(value.runId, 100),
    by: clip(value.by, 100),
    at: typeof value.at === 'number' ? value.at : 0,
    endReason: clip(value.endReason, 40),
    steps,
  };
  if (typeof value.truncated === 'number' && value.truncated > 0) {
    record.truncated = value.truncated;
  }
  return record;
}

/**
 * Shrink a trace to fit `MAX_TRACE_BYTES`, deterministically and worst-first:
 * the bulky optional arguments go before any step disappears, because knowing
 * that a step happened at all is worth more than knowing its arguments.
 */
export function fitTrace(record: TraceRecord): TraceRecord {
  const size = (r: TraceRecord) => JSON.stringify(r).length;
  // Keep the TAIL when over the step cap. A run that went long is one you are
  // reading to find out where it ended up, and the cap is far above any run
  // that fits inside the loop's own tool-call limit anyway.
  let out: TraceRecord = { ...record, steps: record.steps.slice(-MAX_TRACE_STEPS) };
  let dropped = record.steps.length - out.steps.length;

  for (let i = 0; i < out.steps.length && size(out) > MAX_TRACE_BYTES; i++) {
    const { args: _a, opArgs: _o, ...rest } = out.steps[i];
    out = { ...out, steps: out.steps.map((s, j) => (j === i ? rest : s)) };
  }
  while (out.steps.length > 1 && size(out) > MAX_TRACE_BYTES) {
    out = { ...out, steps: out.steps.slice(1) };
    dropped += 1;
  }

  return dropped > 0 ? { ...out, truncated: dropped } : out;
}

/**
 * Record what a run did. One write, at the end of the run — see TraceRecord.
 */
export function writeTrace(doc: Y.Doc, record: TraceRecord): void {
  if (!record.runId) return;
  doc.transact(() => {
    const map = tracesMap(doc);
    map.set(record.runId, fitTrace(record));
    pruneTraces(map);
  }, AGENT_STATE_ORIGIN);
}

function pruneTraces(map: Y.Map<unknown>): void {
  const now = Date.now();
  const records: TraceRecord[] = [];
  map.forEach((value, key) => {
    const record = sanitizeTrace(value);
    if (record) records.push(record);
    else map.delete(key); // junk from a peer: drop it while we are here
  });

  for (const record of records) {
    if (now - record.at > TRACE_TTL_MS) map.delete(record.runId);
  }

  const remaining = records.filter((r) => map.has(r.runId)).sort((a, b) => a.at - b.at);
  let excess = remaining.length - MAX_TRACES;
  for (const record of remaining) {
    if (excess <= 0) break;
    map.delete(record.runId);
    excess -= 1;
  }
}

// ---------------------------------------------------------------------------
// observation
// ---------------------------------------------------------------------------

/** Subscribe to any change under the agent root. Returns an unsubscribe fn. */
export function observeAgent(doc: Y.Doc, onChange: () => void): () => void {
  const root = agentMap(doc);
  const handler = () => onChange();
  root.observeDeep(handler);
  return () => root.unobserveDeep(handler);
}
